import type { ReactNode } from 'react';
import { heuresLisibles, type LignePlan, type PlanFormation as Plan } from '../../lib/useElearning';

/**
 * Plan de formation associé (CDC §7.2) : les blocs du programme, chacun avec
 * ses classes à distance tenues et à venir, et ses replays. Aucun avancement
 * n'est inventé — une séance en présentiel n'est rattachée à aucun bloc, le
 * plan ne dit donc que ce qui s'est fait à distance.
 */
export function PlanFormation<L extends LignePlan>({
  plan,
  detailLigne,
}: {
  plan: Plan<L>;
  /** Contenu sous chaque bloc : ses replays, côté apprenant. */
  detailLigne?: (ligne: L) => ReactNode;
}): JSX.Element {
  if (plan.lignes.length === 0) {
    return (
      <p className="px-4 py-5 text-sm text-codialis-gray sm:px-6">
        Le programme de cette formation n’est pas découpé en blocs : le pôle pédagogique le renseigne dans
        « Formations ».
      </p>
    );
  }

  return (
    <div className="space-y-2 p-4 sm:p-6">
      <p className="text-xs text-codialis-gray">
        À distance : {heuresLisibles(plan.heuresDistanceTenues)} tenues
        {plan.heuresDistanceAVenir > 0 ? ` · ${heuresLisibles(plan.heuresDistanceAVenir)} programmées` : ''}
        {plan.heuresDistancePrevues > 0 ? ` · ${plan.heuresDistancePrevues} h prévues à la formation` : ''}
      </p>
      {plan.lignes.map((ligne) => {
        const termine =
          ligne.heuresProgramme !== null && ligne.heuresProgramme > 0 && ligne.heuresTenues >= ligne.heuresProgramme;
        const statut =
          ligne.classesTenues + ligne.classesAVenir === 0
            ? { texte: 'Aucune classe', ton: 'badge-neutral' }
            : termine
              ? { texte: 'Volume atteint', ton: 'badge-success' }
              : ligne.classesAVenir > 0
                ? { texte: 'Classes à venir', ton: 'badge-info' }
                : { texte: 'Classes tenues', ton: 'badge-success' };
        return (
          <div key={ligne.blocId ?? 'hors-programme'} className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5">
            <div className="text-sm font-medium text-codialis-navy">{ligne.libelle}</div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-codialis-gray">
              <span className={`${statut.ton} whitespace-nowrap rounded px-2 py-0.5 text-[10px] font-bold`}>
                {statut.texte}
              </span>
              <span>
                {ligne.heuresProgramme !== null ? `${ligne.heuresProgramme} h au programme · ` : ''}
                {ligne.classesTenues} tenue{ligne.classesTenues > 1 ? 's' : ''} ({heuresLisibles(ligne.heuresTenues)})
                {ligne.classesAVenir > 0 ? ` · ${ligne.classesAVenir} à venir` : ''}
                {ligne.replays > 0 ? ` · ${ligne.replays} replay${ligne.replays > 1 ? 's' : ''}` : ''}
              </span>
            </div>
            {detailLigne?.(ligne)}
          </div>
        );
      })}
    </div>
  );
}
